import { APP_ORIGIN } from './app-origin.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SLUG = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const FIELDS = ['workspaceId', 'workspaceSlug', 'workspaceName', 'organizationId', 'organizationSlug', 'organizationName', 'effectivePlan', 'apiBaseUrl', 'apiKeysUrl', 'canManageApiKeys'];
const GENERIC_PAIR = '/organizations/example-org/workspaces/example-workspace';
const empty = () => ({ workspaces: [], selected: null });
const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key));

// Mirrors the private App DTO and adds canonical link/target checks. Local hints
// never contribute a slug, an origin, a role or a name to this response.
export function validateDocsContext(value) {
  if (!exactKeys(value, ['workspaces']) || !Array.isArray(value.workspaces) || value.workspaces.length > 100) throw Error('Invalid documentation context');
  const seen = new Set();
  return value.workspaces.map((item) => {
    if (!exactKeys(item, FIELDS) || !UUID.test(item.workspaceId) || !UUID.test(item.organizationId) || seen.has(item.workspaceId)) throw Error('Invalid documentation context');
    seen.add(item.workspaceId);
    for (const field of ['organizationSlug', 'workspaceSlug']) if (typeof item[field] !== 'string' || item[field].length < 2 || item[field].length > 48 || !SLUG.test(item[field])) throw Error('Invalid documentation context');
    for (const field of ['organizationName', 'workspaceName']) if (typeof item[field] !== 'string' || item[field].length < 1 || item[field].length > 512) throw Error('Invalid documentation context');
    if (!['free', 'entry', 'growth'].includes(item.effectivePlan) || typeof item.canManageApiKeys !== 'boolean' || typeof item.apiBaseUrl !== 'string') throw Error('Invalid documentation context');
    const pair = `/organizations/${item.organizationSlug}/workspaces/${item.workspaceSlug}`;
    const url = new URL(item.apiBaseUrl);
    if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash || ![`/v1${pair}`, `/api/v1${pair}`].includes(url.pathname) || url.href !== item.apiBaseUrl) throw Error('Invalid documentation target');
    const apiKeysUrl = `/org/${item.organizationSlug}/workspace/${item.workspaceSlug}/settings/workspace?tab=api-keys`;
    if (item.apiKeysUrl !== apiKeysUrl) throw Error('Invalid documentation link');
    return Object.freeze({ ...item, versionBaseUrl: item.apiBaseUrl.slice(0, -pair.length) });
  });
}

export function rememberedWorkspaceId(storage) {
  try { const value = JSON.parse(storage?.getItem('sw_last_workspace')); return UUID.test(value?.id) ? value.id : null; } catch { return null; }
}

export function trustedDocsPage(pageOrigin, appOrigin) {
  try {
    const app = new URL(appOrigin);
    if (app.href !== `${app.origin}/` || (app.protocol !== 'https:' && !['localhost', '127.0.0.1', '[::1]'].includes(app.hostname))) return false;
    if (pageOrigin === app.origin) return true;
    return (app.origin === 'https://scopeworth.app' && pageOrigin === 'https://www.scopeworth.app') || (app.origin === 'https://staging.scopeworth.app' && pageOrigin === 'https://staging-www.scopeworth.app');
  } catch { return false; }
}

async function boundedJson(response) {
  if (!response.headers?.get('content-type')?.toLowerCase().includes('application/json') || Number(response.headers.get('content-length') || 0) > 262144) throw Error('Invalid context response');
  const reader = response.body?.getReader();
  if (!reader) throw Error('Missing context body');
  const chunks = []; let length = 0;
  try {
    for (;;) { const { done, value } = await reader.read(); if (done) break; length += value.byteLength; if (length > 262144) throw Error('Context response too large'); chunks.push(value); }
  } catch (error) { await reader.cancel().catch(() => {}); throw error; }
  const bytes = new Uint8Array(length); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
}

// All state is per-page memory. A refresh clears displayed context immediately;
// cancelled or out-of-order responses cannot restore an earlier membership.
export function createDocsContextController({ appOrigin = APP_ORIGIN, browserWindow = typeof window === 'undefined' ? null : window, browserDocument = typeof document === 'undefined' ? null : document, fetchImpl = (...args) => fetch(...args), timeoutMs = 5000, setTimeoutImpl = setTimeout, clearTimeoutImpl = clearTimeout, nowImpl = () => performance.now() } = {}) {
  let snapshot = empty(), request = null, preferred = null, resetByUser = false;
  const subscribers = new Set();
  const publish = (next) => { snapshot = next; subscribers.forEach((listener) => listener()); };
  function cancel() { if (!request) return; const old = request; request = null; clearTimeoutImpl(old.timer); old.controller.abort(); }
  async function refresh() {
    cancel(); publish(empty());
    if (!trustedDocsPage(browserWindow?.location?.origin, appOrigin)) return;
    const current = { controller: new AbortController(), timer: null, deadline: nowImpl() + timeoutMs }; request = current;
    current.timer = setTimeoutImpl(() => { if (request === current) { cancel(); preferred = null; publish(empty()); } }, timeoutMs);
    try {
      const response = await fetchImpl(`${new URL(appOrigin).origin}/api/developer-docs/context`, { method: 'GET', credentials: 'include', cache: 'no-store', redirect: 'error', signal: current.controller.signal });
      if (request !== current) return;
      // A background tab can delay timer callbacks. A late response must not
      // restore private context even when the abort timer has not run yet.
      if (nowImpl() >= current.deadline) throw Error('Context deadline exceeded');
      if (response.status !== 200 || response.redirected || response.type === 'opaqueredirect') throw Error('Unavailable context');
      const workspaces = validateDocsContext(await boundedJson(response));
      if (request !== current) return;
      if (nowImpl() >= current.deadline) throw Error('Context deadline exceeded');
      const hint = preferred || rememberedWorkspaceId(browserWindow?.localStorage);
      const selected = resetByUser ? null : (workspaces.find((item) => item.workspaceId === hint) || (workspaces.length === 1 ? workspaces[0] : null));
      preferred = selected?.workspaceId || null;
      publish({ workspaces, selected });
    } catch { if (request === current) { preferred = null; publish(empty()); } }
    finally { clearTimeoutImpl(current.timer); if (request === current) request = null; }
  }
  function select(id) {
    const selected = snapshot.workspaces.find((item) => item.workspaceId === id) || null;
    resetByUser = !selected; preferred = selected?.workspaceId || null;
    publish({ ...snapshot, selected });
  }
  function reset() { cancel(); preferred = null; resetByUser = true; publish(empty()); }
  const onReturn = () => { void refresh(); };
  const onVisibility = () => { if (browserDocument?.visibilityState === 'visible') void refresh(); };
  const onStorage = (event) => { if (event.key === null || event.key === 'sw_last_workspace') { if (event.newValue === null) reset(); else onReturn(); } };
  function listen(add) {
    const method = add ? 'addEventListener' : 'removeEventListener';
    for (const name of ['focus', 'pageshow']) browserWindow?.[method](name, onReturn);
    browserWindow?.[method]('storage', onStorage); browserWindow?.[method]('scopeworth:logout', reset);
    browserDocument?.[method]('visibilitychange', onVisibility);
  }
  return { getSnapshot: () => snapshot, refresh, select, reset,
    subscribe(listener) { subscribers.add(listener); if (subscribers.size === 1) { listen(true); void refresh(); } return () => { subscribers.delete(listener); if (!subscribers.size) { listen(false); cancel(); snapshot = empty(); preferred = null; } }; },
  };
}

// Only the generator's known request URL is changed. Arbitrary guide prose,
// JSON bodies and synthetic record IDs are left as their reviewed source bytes.
export function personalizeSnippets(snippets, selected) {
  if (!selected) return snippets;
  const pair = `/organizations/${selected.organizationSlug}/workspaces/${selected.workspaceSlug}`;
  return snippets.map((snippet) => ({ ...snippet,
    curl: `SCOPEWORTH_API_BASE='${selected.versionBaseUrl}'\n` + snippet.curl.replace(`"$SCOPEWORTH_API_BASE${GENERIC_PAIR}`, `"$SCOPEWORTH_API_BASE${pair}`),
    javascript: `const SCOPEWORTH_API_BASE = ${JSON.stringify(selected.versionBaseUrl)};\n` + snippet.javascript.replace(`process.env.SCOPEWORTH_API_BASE + "${GENERIC_PAIR}`, `SCOPEWORTH_API_BASE + "${pair}`),
    python: `SCOPEWORTH_API_BASE = ${JSON.stringify(selected.versionBaseUrl)}\n` + snippet.python.replace(`os.environ["SCOPEWORTH_API_BASE"] + "${GENERIC_PAIR}`, `SCOPEWORTH_API_BASE + "${pair}`),
  }));
}

export function approvedCodeExamples(original, personalized) {
  const map = new Map();
  original.forEach((snippet, index) => { for (const language of ['curl', 'javascript', 'python']) map.set(snippet[language].trim(), personalized[index][language]); });
  return map;
}

export function personalizeSpec(specification, selected) {
  if (!selected) return specification;
  const spec = structuredClone(specification);
  spec.servers = [{ url: selected.versionBaseUrl, description: 'Selected verified workspace API origin' }];
  for (const path of Object.values(spec.paths)) for (const operation of Object.values(path)) if (operation?.operationId) for (const parameter of operation.parameters || []) {
    if (parameter.in === 'path' && parameter.name === 'organizationSlug') parameter.example = selected.organizationSlug;
    if (parameter.in === 'path' && parameter.name === 'workspaceSlug') parameter.example = selected.workspaceSlug;
  }
  return spec;
}
